import React, { useEffect, useState } from "react";
import { Button } from "@xplatform/design-system";
import { IconSitemap } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { intelligenceEngineApi } from "@services/extractionApi";
import { mappingApi } from "@services/mappingApi";
import { useDoctypeMappingConfig } from "@hooks/extraction/useDoctypeMapping";
import OpenMappingControl from "./OpenMappingControl";

interface RunMappingControlProps {
  processId: string;
  processItemId: string;
  doctypeCode?: string;
  /**
   * The per-run mapping opt-in (`process.mappingEnabled`). When true mapping
   * was already requested at upload time and {@link OpenMappingControl} owns
   * the header slot, so this control renders nothing.
   */
  mappingAlreadyEnabled: boolean;
  /** Gate: a result item is loaded and its ids are available. */
  enabled: boolean;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

/**
 * "Run mapping" call-to-action shown to the left of Export for a completed
 * result whose run did NOT enable mapping, but whose doctype has mapping
 * configured. Clicking dispatches the mapping job on-demand; once dispatched
 * the control hands off to {@link OpenMappingControl}, which polls/subscribes
 * for status and ultimately swaps to the "Open mapping" deep-link.
 *
 * On mount it probes once for an already-existing job (e.g. a previous
 * on-demand run, surviving a page refresh) and hands straight off to the
 * status control when one is found — the per-run `mappingEnabled` flag never
 * flips for on-demand runs, so it can't be used to detect that.
 *
 * Renders nothing when mapping was already enabled for the run, the doctype
 * has no mapping config, or no result is loaded.
 */
const RunMappingControl: React.FC<RunMappingControlProps> = ({
  processId,
  processItemId,
  doctypeCode,
  mappingAlreadyEnabled,
  enabled,
  onSuccess,
  onError,
}) => {
  const { t } = useTranslation();
  const mappingConfig = useDoctypeMappingConfig(doctypeCode);
  const mappingAvailable = Boolean(mappingConfig?.enabled);
  const eligible = enabled && mappingAvailable && !mappingAlreadyEnabled;

  const [dispatching, setDispatching] = useState(false);
  const [dispatched, setDispatched] = useState(false);
  // Until the one-shot existence probe resolves we render nothing rather than
  // flashing the CTA for an item that already has a job in flight.
  const [probing, setProbing] = useState(true);

  useEffect(() => {
    if (!eligible) {
      setProbing(false);
      return;
    }
    let cancelled = false;
    setProbing(true);
    setDispatched(false);
    mappingApi
      .getMappingJobByProcessItem(processId, processItemId)
      .then((job) => {
        if (cancelled) return;
        if (job) setDispatched(true);
      })
      .catch(() => {
        // No job / lookup failed — fall through to the CTA.
      })
      .finally(() => {
        if (!cancelled) setProbing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [eligible, processId, processItemId]);

  // Once dispatched (or a pre-existing job was found), defer entirely to the
  // status control: starting… → progress → Open mapping, like auto-mapping.
  if (dispatched) {
    return (
      <OpenMappingControl
        processId={processId}
        processItemId={processItemId}
        enabled={Boolean(processId && processItemId)}
      />
    );
  }

  if (!eligible || probing) {
    return null;
  }

  const handleRun = async () => {
    setDispatching(true);
    try {
      await intelligenceEngineApi.dispatchMapping(processId, processItemId);
      setDispatched(true);
      onSuccess(t("extraction.mappingStatus.runStarted"));
    } catch (error) {
      console.error("Failed to dispatch mapping:", error);
      onError(t("extraction.mappingStatus.runFailed"));
      setDispatching(false);
    }
  };

  return (
    <Button
      size="xs"
      variant="filled"
      color="brand"
      leftSection={<IconSitemap size={16} />}
      loading={dispatching}
      onClick={handleRun}
      className="run-mapping-button"
    >
      {t("extraction.mappingStatus.runMapping")}
    </Button>
  );
};

export default RunMappingControl;
